import { LatLngLiteral } from "leaflet";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import { OrderExecutingStage, Subscription } from "../../types";
import { Base, Order, CarPark } from "..";
import { ExecutorStatus } from "../Executor";
import {
	ModelEventAction,
	ModelEventConstructor,
	ModelSubscribeEvent,
} from "../../utils/ModelEventConstructor";

const logger = createLogger({ name: "ExecutorLocations" });

const toLatLng = (value: any): LatLngLiteral | undefined => {
	if (!value) return undefined;
	if (Array.isArray(value) && value.length >= 2) {
		return { lng: Number(value[0]), lat: Number(value[1]) };
	}
	if (typeof value === "object") {
		if (value.lat != null && (value.lng != null || value.lon != null)) {
			return {
				lat: Number(value.lat),
				lng: Number(value.lng ?? value.lon),
			};
		}
		if (value[0] != null && value[1] != null) {
			return { lng: Number(value[0]), lat: Number(value[1]) };
		}
	}
	return undefined;
};

const eventModelId = (event: any): number =>
	Number(event?.id ?? event?.data?.id ?? event?.data?.index);

class ExecutorLocations extends Base {
	static fromResponse(data: any): ExecutorLocations.Model {
		const executorId = Number(
			data?.executor?.id ?? data?.executorId ?? data?.id,
		);
		const taxiServiceIdRaw = Number(
			data?.taxiServiceId ?? data?.taxiService?.id,
		);

		return {
			id: Number(data?.id ?? executorId),

			taxiServiceId: Number.isFinite(taxiServiceIdRaw)
				? taxiServiceIdRaw
				: data.taxiServiceId,

			executorId,
			executorCallSign: data?.executorCallSign,

			order: data?.order,

			car: data?.car,
			carId: data?.carId,
			carNumber: data?.carNumber,

			executor: {
				id: executorId,

				orders:
					data?.executor?.executorToOrders?.map(
						(executorToOrder: any) => executorToOrder?.order,
					) ?? undefined,

				online: data?.executor?.online,
				isWorking:
					data?.executor?.workingStatus ?? data?.executor?.isWorking,
				hasActiveOrder: data?.executor?.hasActiveOrder ?? false,
				status: (data?.executor?.status ?? data?.status) as
					| ExecutorStatus
					| undefined,
				alias: data?.executorCallSign ?? data?.executor?.alias,
				rating: data?.rating,
			},
			rating: data?.rating,
			accuracy: data?.accuracy,
			altitude: data?.altitude,
			date: data?.date,
			direction: toLatLng(data?.direction) ?? data?.direction,

			point: toLatLng(data?.point) ?? data?.point,
			speed: data?.speed,
		};
	}

	public static async subscribe(
		options: ExecutorLocations.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<ExecutorLocations.Model>,
	): Promise<Subscription<ExecutorLocations.SubscribeOptions> | null> {
		const mappedByItem = new WeakMap<object, ExecutorLocations.Model>();

		const mapModels = (items: unknown[]): ExecutorLocations.Model[] =>
			items.map((item) => {
				if (typeof item !== "object" || item === null) {
					return this.fromResponse(item);
				}
				const cached = mappedByItem.get(item);
				if (cached) return cached;
				const model = this.fromResponse(item);
				mappedByItem.set(item, model);
				return model;
			});

		const modelEventConstructor = new ModelEventConstructor({
			onUpdate: (state) => {
				onUpdate({
					metadataState: {},
					models: mapModels(state.models),
				});
			},
		});

		const GPS_FLUSH_MS = 200;
		let queuedEvents: any[] = [];
		let flushTimer: ReturnType<typeof setTimeout> | 0 = 0;

		const flushGpsEvents = () => {
			flushTimer = 0;
			if (!queuedEvents.length) return;
			const batch = queuedEvents;
			queuedEvents = [];
			modelEventConstructor.onEvent(batch);
		};

		const enqueueGpsEvents = (events: any) => {
			if (events?.type === "latestLocations") return;
			const raw = Array.isArray(events) ? events : [events];
			const list: ModelSubscribeEvent[] = [];
			for (let i = 0; i < raw.length; i += 1) {
				const event = raw[i];
				if (!event) {
					// skip empty socket frames
				} else if (event.action === ModelEventAction.DELETE) {
					const id = eventModelId(event);
					if (Number.isFinite(id) && id > 0) {
						list.push({
							action: ModelEventAction.DELETE,
							id,
						});
					}
				} else {
					const id = eventModelId(event);
					list.push(
						Number.isFinite(id) && id > 0 && event.id !== id
							? { ...event, id }
							: event,
					);
				}
			}
			if (!list.length) return;

			queuedEvents.push(...list);
			const isInit = list.some(
				(event) => event?.action === ModelEventAction.INIT,
			);
			if (isInit) {
				if (flushTimer) clearTimeout(flushTimer);
				flushGpsEvents();
				return;
			}
			if (!flushTimer) {
				flushTimer = setTimeout(flushGpsEvents, GPS_FLUSH_MS);
			}
		};

		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.executorLocations.subscribe({
					params: this.optionsToRequest(options),
					onEvent: (events: any) => {
						console.log("ExecutorLocations.subscribe", events);
						enqueueGpsEvents(events);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{
				name: "ExecutorLocations.subscribe",
				metadata: this.optionsToRequest(options),
			},
		);

		return {
			unsubscribe: () => {
				if (flushTimer) {
					clearTimeout(flushTimer);
					flushTimer = 0;
				}
				flushGpsEvents();
				subscription.unsubscribe();
			},
			update: (options: ExecutorLocations.SubscribeOptions) =>
				subscription.update(this.optionsToRequest(options)),
		} as Subscription<ExecutorLocations.SubscribeOptions>;
	}

	private static optionsToRequest(
		options: ExecutorLocations.SubscribeOptions,
	) {
		return {
			revealExecutor: options.revealExecutor,
			limit: options.limit,
			offset: options.offset,

			filters: {
				orderIds: options.orderIds,

				executorDebt: options.executorDebt,
				executorWorkingStatus: options.executorWorkingStatus,
				executorHasActiveOrder: options.executorHasActiveOrder,
				executorOnline: options.executorOnline,
				executorStatuses: options.executorStatuses,
				taxiServiceIds: options.taxiServiceIds,
			},
		};
	}
}

namespace ExecutorLocations {
	export interface Model {
		id: number;

		taxiServiceId: number;

		executor: {
			id: number;

			orders?: Order.Model[];

			online: boolean;
			isWorking: boolean;
			hasActiveOrder: boolean;
			status?: ExecutorStatus;
			alias: string;
			rating?: number;
		};
		rating?: number;
		executorId: number;
		executorCallSign: string;

		order?: LocationPointOrder;

		car: {
			callSign: string;
			id: number;
			registrationNumber: string;
		};
		carId: number;
		carNumber: string;

		park?: CarPark.Model; // exists only at the front

		accuracy: number;
		altitude: number;
		date: number;
		direction: LatLngLiteral;

		point: LatLngLiteral;
		speed: number;
	}

	interface LocationPointOrder {
		id: number;
		executingStage: OrderExecutingStage;
	}

	export interface SubscribeOptions {
		orderIds?: number[];

		revealExecutor?: boolean;
		limit?: number;
		offset?: number;

		executorDebt?: boolean;
		executorWorkingStatus?: boolean;
		executorHasActiveOrder?: boolean;
		executorOnline?: boolean;
		executorStatuses?: ExecutorStatus[];
		taxiServiceIds?: number[];
	}
}

export default ExecutorLocations;
